<template>
  <div class="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 auth-background">
    <div class="max-w-md w-full">
      <div class="glassmorphic-card">
        <div class="text-center mb-8">
          <router-link to="/" class="inline-block mb-4">
            <img 
              src="/images/venmas-logo-1.png" 
              alt="Venmas" 
              class="h-16 mx-auto object-contain"
            />
          </router-link>
          <h2 class="text-3xl font-bold text-white mb-2">
            Forgot your password?
          </h2>
          <p class="text-white/70">
            Enter your email address and we'll send you a reset link
          </p>
        </div>
        
        <form class="space-y-6" @submit.prevent="handleForgotPassword">
          <div>
            <label for="email" class="block text-sm font-semibold text-white/90 mb-2">Email Address</label>
            <input
              id="email"
              v-model="email"
              name="email"
              type="email"
              autocomplete="email"
              required
              class="glass-input"
              placeholder="Enter your email address"
            />
          </div>

          <div v-if="error" class="p-4 bg-red-500/20 border border-red-400/30 rounded-lg text-red-200 text-sm text-center">
            {{ error }}
          </div>

          <div v-if="success" class="p-4 bg-green-500/20 border border-green-400/30 rounded-lg text-green-200 text-sm text-center">
            {{ success }}
          </div>

          <div>
            <button
              type="submit"
              :disabled="authStore.loading"
              class="w-full py-3 px-4 text-sm font-medium rounded-xl text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style="background: #2563eb;"
            >
              {{ authStore.loading ? 'Sending...' : 'Send reset link' }}
            </button>
          </div>

          <div class="text-center">
            <router-link
              to="/login"
              class="text-white/80 hover:text-white text-sm transition-colors"
            >
              Back to sign in
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
const email = ref('')
const error = ref('')
const success = ref('')

async function handleForgotPassword() {
  error.value = ''
  success.value = ''
  
  try {
    const response = await authStore.forgotPassword(email.value)
    const responseMessage = (response as { message?: unknown })?.message
    if (typeof responseMessage === 'string') {
      success.value = responseMessage
    } else {
      success.value = 'Password reset link sent successfully.'
    }
    email.value = ''
  } catch (err: unknown) {
    const apiError = (err as { error?: unknown })?.error
    error.value =
      typeof apiError === 'string'
        ? apiError
        : 'Failed to send reset link'
  }
}
</script>

<style scoped>
.auth-background {
  background: #1e3a5f url('/images/auth-bg.png') center / cover no-repeat fixed;
  position: relative;
  isolation: isolate;
}

.auth-background::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 0;
}

.auth-background > * {
  position: relative;
  z-index: 1;
}

.glassmorphic-card {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  padding: 2rem;
  transition: all 0.3s ease;
}

.glassmorphic-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

.glass-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.glass-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.glass-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.2);
}

button[type="submit"]:hover {
  background: #1d4ed8 !important;
}
</style>
